/** biome-ignore-all lint/correctness/useExhaustiveDependencies: <explanation> */
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import useGlobalFilterStore from '@cobliteam/hooks/src/useGlobalFilterStore';
import { Button } from '@cobliteam/ui/components';
import Icons from '@cobliteam/ui/icons';
import { Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';
import { SearchIcon } from './components/SearchIcon';
import GlobalSearchContent from './containers/GlobalSearchContent';
import useFetchData from './hooks/useFetchData';
import { GlobalSearchProvider } from './provider/GlobalSearchProvider';
import { ProviderPersistQueryClient } from './provider/PersisterProvider';

const GlobalSearchFallback = () => {
  return (
    <div className='flex items-center justify-center'>
      <Button data-testid='global-search-button' variant='link' size='sm' color='default' disabled icon={<SearchIcon isHovered={false} />} />
    </div>
  );
};

const GlobalSearchError = () => {
  return (
    <div className='flex items-center justify-center'>
      <Button variant='surface' size='sm' color='danger' icon={<Icons name='x' />} />
    </div>
  );
};

const GlobalSearchTrigger = () => {
  const location = useLocation().pathname;
  const vehicleGroupIds = useGlobalFilterStore((state: any) => state.vehicleGroupIds);
  const { sidebar } = useDashboard();
  const { isLoading, fuse, data, isRefetching, handleUpdateData } = useFetchData({ currentLocation: location, vehicleGroupIds, pagesFlatted: sidebar.pagesFlatted });

  useEffect(() => {
    if (vehicleGroupIds.length > 0) handleUpdateData?.();
  }, [vehicleGroupIds]);

  if (isLoading || !fuse || !data) return <GlobalSearchFallback />;

  return (
    <GlobalSearchProvider fuse={fuse} data={data}>
      <GlobalSearchContent handleUpdateData={handleUpdateData} isRefetching={isRefetching} />
    </GlobalSearchProvider>
  );
};

const GlobalSearch = () => {
  const { runtimeFlags } = useDashboard();
  const hasValidGroupOrVehicles = !(runtimeFlags.noValidGroupOrVehicles ?? false);
  if (!hasValidGroupOrVehicles) return null;

  return (
    <ErrorBoundary
      fallback={<GlobalSearchError />}
      onError={(error, info) => {
        DatadogTrack.error(error, { component: 'GlobalSearch', info });
      }}
    >
      <Suspense fallback={<GlobalSearchFallback />}>
        <ProviderPersistQueryClient>
          <GlobalSearchTrigger />
        </ProviderPersistQueryClient>
      </Suspense>
    </ErrorBoundary>
  );
};

export default GlobalSearch;
